import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * @typedef {object} PurchaseAgreementRefs
 * @property {HTMLElement} scrollBox - The scrollable popup content containing the agreement text.
 * @property {HTMLInputElement} checkbox - The agreement checkbox.
 * @property {HTMLElement} [popup] - The agreement popup overlay.
 * @property {HTMLElement} [hint] - The "scroll to the end" hint text.
 */

// How close to the bottom (in px) counts as "scrolled to the end" — avoids
// requiring pixel-perfect scrolling and absorbs sub-pixel rounding.
const SCROLL_END_THRESHOLD = 2;

// Shown as the checkbox's native validation message while locked — both when
// clicking it directly (via reportValidity) and, as a fallback, if the form
// is ever submitted while it's still checked/locked some other way.
const LOCKED_VALIDATION_MESSAGE = '注意事項を最後までご確認いただきますと、チェックを入れることができます。';

/**
 * Gates a purchase-agreement checkbox behind scrolling its terms box to the end.
 *
 * The checkbox keeps `required` at all times so the browser's native form
 * validation always blocks the add-to-cart / accelerated-checkout buttons
 * while unchecked — only the ability to *check* it is gated here. A `disabled`
 * checkbox would be excluded from constraint validation entirely, which would
 * defeat that protection while scrolling isn't finished yet, so this
 * intercepts the `click` event instead of disabling the input.
 *
 * @extends Component<PurchaseAgreementRefs>
 */
class PurchaseAgreement extends Component {
  requiredRefs = ['scrollBox', 'checkbox'];

  #unlocked = false;

  connectedCallback() {
    super.connectedCallback();

    this.refs.checkbox.setCustomValidity(this.dataset.lockedMessage || LOCKED_VALIDATION_MESSAGE);
    this.refs.scrollBox.addEventListener('scroll', this.#handleScroll, { passive: true });
    // Resize can change whether the content overflows at all (e.g. font loading,
    // viewport resize), so re-check rather than only checking once on connect.
    this.#resizeObserver.observe(this.refs.scrollBox);

    this.#closestSection?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.refs.scrollBox.removeEventListener('scroll', this.#handleScroll);
    this.#resizeObserver.disconnect();

    this.#closestSection?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  get #closestSection() {
    return this.closest('.shopify-section, dialog');
  }

  #resizeObserver = new ResizeObserver(() => this.#checkScrollPosition());

  #handleScroll = () => {
    this.#checkScrollPosition();
  };

  #checkScrollPosition() {
    if (this.#unlocked) return;

    const { scrollBox } = this.refs;

    // While the popup is closed (display: none), scrollHeight/clientHeight/scrollTop are
    // all 0, which would otherwise satisfy "scrolled to end" trivially and unlock the
    // checkbox before the agreement has ever been opened, let alone read.
    if (scrollBox.clientHeight === 0) return;

    const scrolledToEnd =
      scrollBox.scrollHeight - scrollBox.scrollTop - scrollBox.clientHeight <= SCROLL_END_THRESHOLD;

    if (scrolledToEnd) {
      this.#unlock();
    }
  }

  #unlock() {
    this.#unlocked = true;
    this.classList.remove('locked');
    this.refs.checkbox.removeAttribute('aria-disabled');
    this.refs.checkbox.setCustomValidity('');
  }

  /**
   * Blocks checking the box (mouse, touch, and keyboard space/enter all fire a
   * `click` event on a checkbox) until the terms have been scrolled to the end.
   * @param {MouseEvent} event
   */
  handleCheckboxClick(event) {
    if (this.#unlocked) return;

    event.preventDefault();
    this.refs.checkbox.reportValidity();
    this.refs.scrollBox.focus();
  }

  openPopup() {
    this.refs.popup?.toggleAttribute('open', true);
    // The popup was just made visible, so dimensions are now real — check whether the
    // agreement already fits without scrolling (nothing more to read) or is still locked.
    this.#checkScrollPosition();
  }

  closePopup() {
    this.refs.popup?.toggleAttribute('open', false);
  }

  /**
   * Closes the popup only when the click landed directly on the backdrop (this element
   * itself), not when it bubbled up from a click inside `.purchase-agreement__popup-content`.
   * @param {MouseEvent} event
   */
  handleBackdropClick(event) {
    if (event.target !== this.refs.popup) return;

    this.closePopup();
  }

  /**
   * Shows/hides the preorder notice for the newly selected variant, based on
   * the product's 「受注生産」 tag re-rendered server-side.
   * @param {import('@shopify/events').ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        if (detail.productId && detail.productId !== this.dataset.productId) return;

        const newComponent = detail.html.querySelector(
          `purchase-agreement-component[data-block-id="${this.dataset.blockId}"]`
        );
        if (!newComponent) return;

        const newPreorder = newComponent.querySelector('[ref="preorder"]');
        const currentPreorder = this.querySelector('[ref="preorder"]');

        if (!newPreorder) {
          currentPreorder?.remove();
        } else if (!currentPreorder) {
          this.prepend(newPreorder.cloneNode(true));
        } else {
          currentPreorder.replaceWith(newPreorder.cloneNode(true));
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[purchase-agreement] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('purchase-agreement-component')) {
  customElements.define('purchase-agreement-component', PurchaseAgreement);
}
